import {
  AutoFeedbackResponse,
  AutoFeedbackItemTransformed,
  GetStepCompatibilityRequest,
  GetStepCompatibilityResponse,
  validAutoFeedbackStepTypes,
  AutoFeedbackItemStatus,
  InteractionStepType,
} from '@scale/shared/types/chat';
import { AutoFeedbackStatusState } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/components/AutoFeedbackStatus';
import { UseAutoFeedbackResponse } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/hooks/useAutoFeedback';
import { FetchFeedbackParams } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/types';
import apiFetch from 'react-scale/lib/api-fetch';

export const fetchFeedback = async ({
  payload,
  onOpen,
  onMessage,
  onClose,
  onError,
}: FetchFeedbackParams): Promise<AutoFeedbackResponse> => {
  return apiFetch<AutoFeedbackResponse>(null, `internal/genai/autoFeedback`, {
    body: payload,
    maxRetries: 2,
    method: 'POST',
    parseAndHandle: true,
    redirectOnUnauthorized: true,
    fetchEventSourceOptions: payload.stream
      ? {
          openWhenHidden: true,
          onopen: onOpen,
          onmessage: onMessage,
          onclose: onClose,
          onerror: onError,
        }
      : undefined,
  });
};

export const getStepCompatibility = (
  payload: GetStepCompatibilityRequest,
): Promise<GetStepCompatibilityResponse> => {
  return apiFetch<GetStepCompatibilityResponse>(null, `internal/genai/getStepCompatibility`, {
    body: payload,
    maxRetries: 2,
    method: 'POST',
    parseAndHandle: true,
    redirectOnUnauthorized: true,
  });
};

export const joinFeedback = (
  originalFeedback?: AutoFeedbackItemTransformed[],
  newFeedback?: AutoFeedbackItemTransformed[],
): AutoFeedbackItemTransformed[] | null => {
  if (!originalFeedback && !newFeedback) {
    return null;
  }

  const disagreeFeedback = originalFeedback?.filter(
    f => f.status === AutoFeedbackItemStatus.IGNORED,
  );

  return [...(newFeedback || []), ...(disagreeFeedback || [])];
};

export const getAutoFeedbackStatus = (
  autoFeedbackHook?: UseAutoFeedbackResponse,
): AutoFeedbackStatusState => {
  if (!autoFeedbackHook) {
    return AutoFeedbackStatusState.NOT_RUN;
  }

  const feedbackToDisplay = (autoFeedbackHook?.feedback ?? []).filter(
    f => f.status !== AutoFeedbackItemStatus.RESOLVED,
  );
  const feedbackToResolve = (autoFeedbackHook?.feedback ?? []).filter(
    f => f.status === AutoFeedbackItemStatus.FIXED,
  );

  const computeAutoFeedbackStatus = () => {
    if (autoFeedbackHook?.isFetchingFeedback) {
      return AutoFeedbackStatusState.LOADING;
    } else if (autoFeedbackHook?.error) {
      return AutoFeedbackStatusState.ERROR;
    } else if (feedbackToResolve.length) {
      return AutoFeedbackStatusState.REVIEW_ISSUES;
    } else if (!autoFeedbackHook?.feedback || !feedbackToDisplay) {
      return AutoFeedbackStatusState.NOT_RUN;
    } else {
      return AutoFeedbackStatusState.PASSED;
    }
  };

  return computeAutoFeedbackStatus();
};

export const validateAutoFeedbackStepType = (stepType: InteractionStepType) => {
  if (!validAutoFeedbackStepTypes.includes(stepType)) {
    throw new Error(`Unsupported step type for Chat Companion: ${stepType}`);
  }
};
